import { ApiResponse } from '@nohara-dxdev/oidc-core';
import { QueryConfig } from '@nohara-dxdev/react-core';
import { useScopeIds } from '@nohara-dxdev/react-oidc';
import { queryOptions, useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api-client';

export interface ItemTemplateVersionEntry {
  version: number;
  isCurrent: boolean;
  updatedAt?: number;
  snapshotAt?: number;
  snapshotBy?: string;
}

export interface ItemTemplateVersionsResponse {
  currentVersion: number;
  versions: ItemTemplateVersionEntry[];
}

export const getItemTemplateVersions = async (
  orgId: string,
  itemTemplateId: string,
): Promise<ItemTemplateVersionsResponse> => {
  const { data } = await api.get<undefined, ApiResponse<ItemTemplateVersionsResponse>>(
    `/orgs/${orgId}/item-templates/${itemTemplateId}/versions`,
  );
  return data;
};

export const getItemTemplateVersionsQueryKey = (
  orgId?: string | null,
  itemTemplateId?: string | null,
) => ['item-template-versions', orgId, itemTemplateId];

export const getItemTemplateVersionsQueryOptions = (
  orgId: string | undefined | null,
  itemTemplateId: string | undefined | null,
) =>
  queryOptions({
    queryKey: getItemTemplateVersionsQueryKey(orgId, itemTemplateId),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: () => getItemTemplateVersions(orgId!, itemTemplateId!),
    enabled: !!orgId && !!itemTemplateId,
  });

interface UseItemTemplateVersionsOptions {
  queryConfig?: QueryConfig<typeof getItemTemplateVersionsQueryOptions>;
}

export const useItemTemplateVersions = (
  itemTemplateId: string | undefined,
  { queryConfig }: UseItemTemplateVersionsOptions = {},
) => {
  const { orgId } = useScopeIds('org');
  return useQuery({
    ...getItemTemplateVersionsQueryOptions(orgId, itemTemplateId),
    ...queryConfig,
  });
};
